import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useVoicePreferences } from '../hooks/useVoicePreferences';
import VoiceSelector from './VoiceSelector';

const VoiceSettings = () => {
  const navigate = useNavigate();
  const { session, profile, loading: authLoading } = useAuth();
  const [selectedGender, setSelectedGender] = useState('female');
  const [saveStatus, setSaveStatus] = useState('');
  
  // Get user ID from session
  const userId = session?.user?.id;
  
  const {
    voicePreferences: userVoices,
    loading: isLoading,
    updateVoicePreferences,
    refreshPreferences: loadVoicePreferences
  } = useVoicePreferences(userId);

  useEffect(() => {
    if (userId) {
      loadVoicePreferences();
    }
  }, [userId, loadVoicePreferences]);

  const handleSave = async () => {
    if (!userId) {
      setSaveStatus('error|Please log in to save voice preferences');
      return;
    }

    try {
      setSaveStatus('saving');
      await updateVoicePreferences(userVoices);
      setSaveStatus('success|Voice preferences saved successfully!');
      setTimeout(() => setSaveStatus(''), 3000);
    } catch (error) {
      console.error('Error saving voice preferences:', error);
      setSaveStatus('error|Failed to save voice preferences. Please try again.');
      setTimeout(() => setSaveStatus(''), 5000);
    }
  };

  const handleVoiceChange = (gender, voiceId) => {
    const newVoices = { ...userVoices, [gender]: voiceId };
    // Update the hook's state directly
    updateVoicePreferences(newVoices);
  };

  if (authLoading) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Loading...</h2>
      </div>
    );
  }

  if (!session) {
    return (
      <div style={{ padding: '20px', textAlign: 'center' }}>
        <h2>Please log in to access voice settings</h2>
        <button onClick={() => navigate('/login')}>Go to Login</button>
      </div>
    );
  }

  return (
    <div style={{ 
      padding: '20px', 
      maxWidth: '800px', 
      margin: '0 auto',
      fontFamily: 'Arial, sans-serif'
    }}>
      <button 
        onClick={() => navigate('/settings')}
        style={{
          marginBottom: '20px',
          padding: '10px 15px',
          backgroundColor: '#6c757d',
          color: 'white',
          border: 'none',
          borderRadius: '5px',
          cursor: 'pointer'
        }}
      >
        ← Back to Settings
      </button>
      
      <h1 style={{ color: '#333', marginBottom: '20px' }}>Voice Settings</h1>
      
      <div style={{
        backgroundColor: '#f8f9fa',
        padding: '20px',
        borderRadius: '10px',
        border: '1px solid #dee2e6'
      }}>
        <h2 style={{ color: '#495057', marginBottom: '15px' }}>Voice Customization</h2>
        <p style={{ color: '#6c757d', lineHeight: '1.6', marginBottom: '20px' }}>
          Customize your wellness rep's voice preferences here. Select different voices for 
          male and female wellness reps to create a more personalized wellness experience.
        </p>
        
        {isLoading ? (
          <div style={{ textAlign: 'center', padding: '20px' }}>
            <p>Loading voice preferences...</p>
          </div>
        ) : (
          <>
            {/* Gender Selection */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#495057', marginBottom: '10px' }}>Select Gender to Customize</h3>
              <div style={{ display: 'flex', gap: '10px', marginBottom: '20px' }}>
                <button 
                  onClick={() => setSelectedGender('female')}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: selectedGender === 'female' ? '#007bff' : '#e9ecef',
                    color: selectedGender === 'female' ? 'white' : '#495057',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  👩‍⚕️ Female Wellness Rep
                </button>
                <button 
                  onClick={() => setSelectedGender('male')}
                  style={{
                    padding: '10px 20px',
                    backgroundColor: selectedGender === 'male' ? '#28a745' : '#e9ecef',
                    color: selectedGender === 'male' ? 'white' : '#495057',
                    border: 'none',
                    borderRadius: '5px',
                    cursor: 'pointer'
                  }}
                >
                  👨‍⚕️ Male Wellness Rep
                </button>
              </div>
            </div>

            {/* Voice Selection */}
            <div style={{ marginBottom: '20px' }}>
              <h3 style={{ color: '#495057', marginBottom: '10px' }}>
                Voice for {selectedGender === 'female' ? 'Female' : 'Male'} Wellness Rep
              </h3>
              <VoiceSelector
                currentGender={selectedGender}
                selectedVoice={userVoices?.[selectedGender] || ''}
                onVoiceChange={(voiceId) => handleVoiceChange(selectedGender, voiceId)}
              />
            </div>

            {/* Current Preferences Display */}
            {userVoices && (
              <div style={{
                backgroundColor: '#e9ecef',
                padding: '15px',
                borderRadius: '5px',
                marginBottom: '20px'
              }}>
                <h4 style={{ margin: '0 0 10px 0', color: '#495057' }}>Current Preferences:</h4>
                <p style={{ margin: '5px 0', color: '#495057' }}>
                  👩‍⚕️ Female Wellness Rep: {userVoices.female || 'Not selected'}
                </p>
                <p style={{ margin: '5px 0', color: '#495057' }}>
                  👨‍⚕️ Male Wellness Rep: {userVoices.male || 'Not selected'}
                </p>
              </div>
            )}

            {/* Save Button */}
            <button
              onClick={handleSave}
              disabled={saveStatus.includes('saving')}
              style={{
                padding: '12px 30px',
                backgroundColor: saveStatus.includes('saving') ? '#6c757d' : '#28a745',
                color: 'white',
                border: 'none',
                borderRadius: '5px',
                cursor: saveStatus.includes('saving') ? 'not-allowed' : 'pointer',
                fontSize: '16px',
                marginRight: '10px'
              }}
            >
              {saveStatus.includes('saving') ? 'Saving...' : 'Save Voice Preferences'}
            </button>

            {/* Status Messages */}
            {saveStatus && (
              <div style={{
                marginTop: '15px',
                padding: '10px',
                borderRadius: '5px',
                backgroundColor: saveStatus.startsWith('success') ? '#d4edda' : 
                               saveStatus.startsWith('error') ? '#f8d7da' : '#fff3cd',
                color: saveStatus.startsWith('success') ? '#155724' : 
                       saveStatus.startsWith('error') ? '#721c24' : '#856404',
                border: `1px solid ${saveStatus.startsWith('success') ? '#c3e6cb' : 
                                    saveStatus.startsWith('error') ? '#f5c6cb' : '#ffeaa7'}`
              }}>
                {saveStatus.split('|')[1] || saveStatus}
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default VoiceSettings;
